import React, { useRef, useState } from "react";

import { Box, Button, Chip, Popover, Stack, TextField, Typography } from "@mui/material";
import { SelectAutocomplete } from "components/forms";
import type { Filters } from "store/types";

import { useFilterOptions } from "../hooks/useFilterOptions";

interface GlobalFilterBarProps {
  filters: Filters;
  onFiltersChange: (filters: Filters) => void;
}

type FilterKey = "investors"; // | "assetClasses";

const FILTER_CHIPS: { key: FilterKey; label: string }[] = [
  { key: "investors", label: "Investor" }
  // { key: "assetClasses", label: "Asset Class" }
];

export const GlobalFilterBar: React.FC<GlobalFilterBarProps> = ({ filters, onFiltersChange }) => {
  const {
    investorOptions
    // assetClassOptions
  } = useFilterOptions();
  const [editKey, setEditKey] = useState<FilterKey | null>(null);
  const [editValues, setEditValues] = useState<string[]>([]);
  const anchorRef = useRef<HTMLElement | null>(null);

  const optionsMap: Record<FilterKey, string[]> = {
    investors: investorOptions
    // assetClasses: assetClassOptions
  };

  const openFilter = (e: React.MouseEvent<HTMLElement>, key: FilterKey) => {
    anchorRef.current = e.currentTarget;
    setEditKey(key);
    setEditValues(filters[key]);
  };

  const handleSave = () => {
    if (!editKey) return;
    onFiltersChange({ ...filters, [editKey]: editValues });
    setEditKey(null);
  };

  const handleClose = () => {
    setEditKey(null);
    setEditValues([]);
  };

  const handleDelete = (key: FilterKey) => {
    onFiltersChange({ ...filters, [key]: [] });
  };

  const editLabel = FILTER_CHIPS.find((c) => c.key === editKey)?.label || "";

  return (
    <>
      {FILTER_CHIPS.map(({ key, label }) => {
        const count = filters[key].length;
        return (
          <Chip
            key={key}
            size="small"
            sx={{ height: "26px", fontSize: "15px", color: "#0a3142", bgcolor: "#F6F8FC" }}
            label={count > 0 ? `${label} (${count})` : label}
            onClick={(e) => openFilter(e, key)}
            {...(count > 0 ? { onDelete: () => handleDelete(key) } : {})}
          />
        );
      })}

      <Popover
        open={!!editKey}
        anchorEl={anchorRef.current}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        onClose={handleClose}
        disableEnforceFocus
        disableAutoFocus
      >
        <Stack sx={{ alignItems: "stretch", p: "30px", gap: "30px", maxWidth: "500px" }}>
          <Stack
            sx={{ borderBottom: "1px solid #dae0e2", pb: "5px" }}
            flexDirection="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography sx={{ fontSize: "15px" }}>{editLabel}</Typography>
            <Stack flexDirection="row">
              <Button sx={{ color: "#26A6AE" }} variant="text" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                sx={{ color: "#26A6AE", ":disabled": { backgroundColor: "#FFF", color: "#bac7ce" } }}
                variant="text"
                onClick={handleSave}
              >
                Save
              </Button>
            </Stack>
          </Stack>
          <SelectAutocomplete<string, true>
            multiple
            disableCloseOnSelect
            selectAll
            limitTags={1}
            openOnFocus
            options={editKey ? optionsMap[editKey] : []}
            renderInput={(params) => <TextField {...params} label={editLabel} />}
            renderTags={(tagValue, getTagProps) => (
              <Box sx={{ maxHeight: "160px", overflowY: "auto", overflowX: "hidden" }}>
                {tagValue.map((option, index) => (
                  <Chip {...getTagProps({ index })} key={index} label={option} />
                ))}
              </Box>
            )}
            sx={{ maxWidth: "300px", minWidth: "300px" }}
            value={editValues}
            setValue={(value) => setEditValues(value)}
          />
        </Stack>
      </Popover>
    </>
  );
};
